import React, { ForwardRefRenderFunction, forwardRef, useMemo, useState } from "react";
//#region redux
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { DimensionEnums } from "models/enums";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import {
  IncomingInvoiceDetailsFormContainerProps,
  IncomingInvoiceUpdatePaymentFormContainerProps,
} from "models/propTypes/incomingInvoicePropTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, TodoConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
//#region  components
import useInvoiceCategorySuggestions from "../useInvoiceCategorySuggestions";
import useUpdatePaymentDialog from "../useUpdatePaymentDialog";
import useEditableState from "../useReadOnlyState";
import useIncomingInvoiceTodoFormHelpers from "./useTodoFormHelpers";
import { periodisationRowsSchema, useDimensionMaps, useNavigationHistory } from "components/common";
import IncomingInvoiceDetailsForm from "./detailsForm";
import { useBookingTreeService } from "serviceHooks";
import useIncomingInvoicePermissions from "../usePermissions";
import { getCarriedCostForwardingValues } from "components/costForwardingMarking";
//#endregion
import * as Yup from "yup";
import {
  LocalizationHelperInstance,
  DateHelper,
  DimensionHelper,
  PeriodisationRowsHelper,
  UtilsHelper,
} from "utils/helpers";
import useBookingTreeItems from "../bookingTree/useBookingTreeItems";

const IncomingInvoiceDetailsFormContainer: ForwardRefRenderFunction<
  FormHandles,
  IncomingInvoiceDetailsFormContainerProps
> = (props, ref) => {
  const bookingTreeService = useBookingTreeService();

  const { getHasIncomingInvoiceSigneePermission } = useIncomingInvoicePermissions();
  const hasPermissionToEditPaymentInformation = getHasIncomingInvoiceSigneePermission();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const { invoice, onUpdatePaymentDialogSuccessHandler } = props;

  const companyNoVat = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const [dimensionAmount, setDimensionAmount] = useState<number>(
    PeriodisationRowsHelper.netAmount(invoice.amount, invoice.tax, companyNoVat)
  );

  const paymentDate = invoice.paymentDate ?? invoice.toPayDate ?? invoice.dueDate;
  const { defaultAccount, suggestions: accountSuggestions } = useInvoiceCategorySuggestions<AutocompleteListItem>(
    invoice.id,
    invoice.isAutoInvoice,
    bookingTreeService.convertToAutoCompleteItems
  );

  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.IncomingInvoice);

  const isReadOnly = useEditableState(invoice.state);
  const isUnhandled =
    invoice.state === WintSuperkollAPIModel.IncomingInvoiceState.Unhandled ||
    invoice.state === WintSuperkollAPIModel.IncomingInvoiceState.SentForProcessing;

  const { getAccountAdditionalInfoType } = useIncomingInvoiceTodoFormHelpers(invoice);

  const isInternationalPayment = invoice?.isInternationalPayment ?? false;
  const isSEPAPayment = invoice?.isSEPAPayment ?? false;

  // Read on the first render: the form's own effect, which runs before this container's, removes the
  // route state. Kept in the initial values so that a reinitialisation, such as when the booking tip
  // arrives, does not approve the invoice without the marking chosen in the to-do.
  const { getRouteStateValue } = useNavigationHistory();
  const [carriedCostForwarding] = useState(() =>
    getCarriedCostForwardingValues(getRouteStateValue(TodoConstants.RouteTodoSelectionKey))
  );

  const initialValues = useMemo(
    (): IncomingInvoiceFormTypes.Details => ({
      supplierName: invoice.supplier?.name ?? "",
      supplierOrgNr: invoice.supplier?.orgNr ?? "",
      account:
        invoice.account || defaultAccount
          ? {
              id: invoice.account || (defaultAccount && (defaultAccount.id as number)) || undefined,
              value: (invoice.accountName || (defaultAccount && (defaultAccount.value as string)) || undefined) as string,
            }
          : undefined,
      referencePerson: invoice.referencePerson ?? "",
      vat: Math.abs(invoice.tax),
      amount: invoice.amount,
      currency: invoice.currency,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      bg: invoice.bg,
      ocr: invoice.oCR,
      paymentDate: paymentDate,
      paymentTerms: invoice.dueDate ? DateHelper.getDiffDays(invoice.invoiceDate, invoice.dueDate) : undefined,
      paymentStatus:
        invoice.isAlreadyPaid || invoice.state === WintSuperkollAPIModel.IncomingInvoiceState.Paid
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Paid)
          : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Unpaid),
      isAlreadyPaid: invoice.isAlreadyPaid,
      isCredit: invoice.isCredit,
      dimensions: dimensions ? DimensionHelper.getValidList(invoice.dimensionList ?? [], dimensions) : [],
      periodisation: PeriodisationRowsHelper.fromInvoice(invoice),
      allowPeriodisation: invoice.allowPeriodisation,
      additionalInfoComment: invoice.additionalInfoComment,
      iban: invoice.iBAN,
      bic: invoice.bIC ?? invoice.supplier?.bIC,
      isInternationalPaymentDetailsAccepted: false,
      street: invoice.supplier?.street,
      street2: invoice.supplier?.street2,
      city: invoice.supplier?.city,
      zipCode: invoice.supplier?.zipCode,
      country: invoice.supplier?.country,
      ...carriedCostForwarding,
    }),
    [invoice, defaultAccount, dimensions, carriedCostForwarding]
  );

  const { searchBookingTreeItemsAutoComplete: getBookingTreeItems } = useBookingTreeItems();

  //#region event handlers
  const updatePaymentDialog = useUpdatePaymentDialog();
  const onUpdatePaymentClick = (formCurrentValues: IncomingInvoiceFormTypes.Details) => {
    const updatePaymentFormValues: IncomingInvoiceUpdatePaymentFormContainerProps = {
      invoiceId: props.invoice.id,
      isAlreadyPaid: formCurrentValues?.isAlreadyPaid ?? props.invoice.isAlreadyPaid,
      paymentDate: formCurrentValues?.paymentDate ?? props.invoice.toPayDate,
    };
    dispatch(displayDialog(updatePaymentDialog(updatePaymentFormValues, onUpdatePaymentDialogSuccessHandler)));
  };
  //#endregion

  const supplierAddressField = () =>
    isInternationalPayment && hasPermissionToEditPaymentInformation
      ? Yup.string().required(TranslationKeys.Validations.Required)
      : Yup.string();

  const validationSchema = useMemo(
    () =>
      Yup.object<Partial<IncomingInvoiceFormTypes.Details>>().shape(
        {
          supplierName: Yup.string(),
          supplierOrgNr: Yup.string(),
          account: Yup.object<AutocompleteListItem>({
            value: Yup.string(),
            id: Yup.number().required(TranslationKeys.Validations.Required),
          })
            .default(undefined)
            .required(TranslationKeys.Validations.Required),
          referencePerson: Yup.string(),
          amountExcludingVat: Yup.number().min(0.0, TranslationKeys.Validations.Required),
          vat: Yup.number().default(0).min(0.0, TranslationKeys.Validations.Required),
          amount: Yup.number()
            .required(TranslationKeys.Validations.Required)
            .min(0.0, TranslationKeys.Validations.Required),
          currency: Yup.string().required(TranslationKeys.Validations.Required),
          invoiceDate: Yup.date()
            .required(TranslationKeys.Validations.Required)
            .typeError(TranslationKeys.Validations.InvalidDate),
          dueDate: Yup.date()
            .typeError(TranslationKeys.Validations.InvalidDate)
            .min(
              Yup.ref(FormFieldConstants.IncomingInvoice.Details.InvoiceDate),
              TranslationKeys.Validations.InvoiceDueDateGreaterThanInvoiceDate
            ),
          bg: Yup.string(),
          ocr: Yup.string(),
          paymentDate: Yup.date()
            .typeError(TranslationKeys.Validations.InvalidDate)
            .min(DateHelper.resetTime(DateHelper.today()) as Date, TranslationKeys.Validations.ShouldBeFutureDate),
          paymentTerms: Yup.number().typeError(TranslationKeys.Validations.InvalidPaymentTerms),
          isAlreadyPaid: Yup.boolean(),
          paymentStatus: Yup.string(),
          isCredit: Yup.boolean(),
          dimensions: Yup.array()
            .of(
              Yup.object({
                dimensionId: Yup.string(),
                amount: Yup.number(),
              })
            )
            .checkForcedDimensions(forcedDimensionMap)
            .validateDimensionAmounts(dimensions, dimensionAmount),
          periodisation: periodisationRowsSchema(Math.abs(dimensionAmount)),
          additionalInfoComment: Yup.string()
            .when(
              FormFieldConstants.IncomingInvoice.Details.Account,
              ([account], schema: Yup.StringSchema) =>
                (!invoice.isAutoInvoice &&
                  getAccountAdditionalInfoType(account?.id) ===
                    WintSuperkollAPIModel.AdditionalInformationRequired.Comment &&
                  schema.required(TranslationKeys.Validations.Required)) ||
                schema
            )
            .when(
              [
                FormFieldConstants.IncomingInvoice.Details.Account,
                FormFieldConstants.IncomingInvoice.Details.AdditionalInfoAttachments,
              ],
              ([account, additionaAttachment], schema: Yup.StringSchema) =>
                (!invoice.isAutoInvoice &&
                  getAccountAdditionalInfoType(account?.id) ===
                    WintSuperkollAPIModel.AdditionalInformationRequired.FileOrComment &&
                  !additionaAttachment?.length &&
                  schema.required(TranslationKeys.Validations.Required)) ||
                schema
            ),
          additionalInfoAttachments: Yup.array()
            .of(
              Yup.object<UploadedFile>({
                contentType: Yup.string(),
                fileName: Yup.string(),
                fileSize: Yup.number(),
                data: Yup.string(),
              })
            )
            .when(
              [
                FormFieldConstants.IncomingInvoice.Details.Account,
                FormFieldConstants.IncomingInvoice.Details.AdditionalInfoComment,
              ],
              ([account, additionalComment]: [AutocompleteListItem, string], schema: ArraySchema<UploadedFile>) =>
                (!invoice.isAutoInvoice &&
                  getAccountAdditionalInfoType(account?.id) ===
                    WintSuperkollAPIModel.AdditionalInformationRequired.FileOrComment &&
                  UtilsHelper.isEmpty(additionalComment) &&
                  schema.required(TranslationKeys.Validations.Required)) ||
                schema
            ),
          iban: Yup.string().when("$", (value, schema: Yup.StringSchema) =>
            isInternationalPayment && hasPermissionToEditPaymentInformation
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
          ),
          bic: Yup.string().when("$", (value, schema: Yup.StringSchema) =>
            isInternationalPayment && !isSEPAPayment && hasPermissionToEditPaymentInformation
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
          ),
          isInternationalPaymentDetailsAccepted:
            isInternationalPayment && hasPermissionToEditPaymentInformation
              ? Yup.boolean().oneOf([true], TranslationKeys.Validations.Required)
              : Yup.boolean(),
          street: supplierAddressField(),
          street2: Yup.string(),
          city: supplierAddressField(),
          zipCode: supplierAddressField(),
          country: supplierAddressField(),
        },
        [
          [
            FormFieldConstants.IncomingInvoice.Details.AdditionalInfoComment,
            FormFieldConstants.IncomingInvoice.Details.AdditionalInfoAttachments,
          ],
        ]
      ),
    [
      dimensions,
      forcedDimensionMap,
      dimensionAmount,
      isInternationalPayment,
      hasPermissionToEditPaymentInformation,
      isSEPAPayment,
    ]
  );

  return (
    <IncomingInvoiceDetailsForm
      ref={ref}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      invoice={invoice}
      dimensionAmount={dimensionAmount}
      setDimensionAmount={setDimensionAmount}
      accountSuggestions={accountSuggestions}
      getBookingTreeItems={getBookingTreeItems}
      updatePaymentClickHandler={onUpdatePaymentClick}
      onPaymentDetailsAcceptHandler={props.onPaymentDetailsAcceptHandler}
      isReadOnly={isReadOnly}
      hasPermissionToEditPaymentInformation={hasPermissionToEditPaymentInformation}
      onFormValidationChange={props.onFormValidationChange}
      fileViewer={props.fileViewer}
      companyNoVat={companyNoVat}
      showDimensions={dimensions && dimensions.length > 0}
      isUnhandled={isUnhandled}
      loadInvoice={props.loadInvoice}
    />
  );
};

export default forwardRef(IncomingInvoiceDetailsFormContainer);
